import React, { useEffect, useState } from 'react';
import { useMsal } from "./auth/msalExports";
import './App.scss';
import { getCurrentUser } from "./ducks/user";
import { useDispatch, useSelector } from "react-redux";
import { acquireAccessToken } from './services/api/auth'
import { AppLoadContext } from './components/ui/AppLoadContext'
import { apiInstance } from './services/apiInstance'
import { msalInstance } from "./index";
import SessionTimeout from "./components/SessionTimeout";
//import { getPowerBiEmbededUrls } from "./ducks/report";
import * as SessionHandler from "./services/sessionHandler";
import Pages from "./views/Pages";
import { getStarPlannerShinyURL } from "./ducks/quality-management";
import Loader from "./components/ui/Loader";
import AlertModal from "./components/ui/AlertModal";
import { AlertContext } from "./context/AlertContext";
import { SubscriptionContext } from "./context/SubscriptionContext";
import { getCompanySubscriptions } from './ducks/company';

const isAuthBypass = process.env.REACT_APP_AUTH_BYPASS === 'true';
const LOGOUT_TIMEOUT_TIMER = 300000; // 300 seconds

// Only import MSAL browser types when NOT bypassing auth.
// Importing @azure/msal-browser triggers module-level initialization
// that tries to contact Azure B2C, which fails in docker mode.
let EventType, InteractionType, b2cPolicies, loginRequest;
if (!isAuthBypass) {
    ({ EventType, InteractionType } = require("@azure/msal-browser"));
    ({ b2cPolicies, loginRequest } = require("./msalAuthConfig"));
}

function App() {
    const { instance } = useMsal();
    const dispatch = useDispatch();
    const [isAppLoaded, setIsAppLoaded] = useState(false);
    const [appSubscription, setAppSubscription] = useState(null);
    const [alert, setAlert] = useState({ show: false, message: '', type: null });

    const currentUser = useSelector((state) => state.user.currentUser);
    const { subscriptions } = useSelector(state => state.company);

    let fetchData = React.useCallback(async () => {
        setIsAppLoaded(false);
        const accessToken = await acquireAccessToken(msalInstance);
        if (accessToken) {
            setIsAppLoaded(true);
            apiInstance.setAuth(accessToken);

            // all reports are disabled for now
           // dispatch(getPowerBiEmbededUrls());
            dispatch(getStarPlannerShinyURL());

            dispatch(getCurrentUser(instance || msalInstance, isAuthBypass ? null : async () => {
                return instance.logoutRedirect({
                    postLogoutRedirectUri: `${window.location.origin}/error-redirect`,
                });
            }));
        }
    }, [dispatch]);

    const handleShowAlert = (message, type) => {
        setAlert({ show: true, message: message, type: type });
    }

    const handleHideAlert = () => {
        setAlert({ ...alert, show: false });
    }

    const handleExitAlert = () => {
        setAlert({ ...alert, message: '', type: null });
    }

    useEffect(() => {
        apiInstance.setContext(handleShowAlert);
    }, [])

    useEffect(() => {
        fetchData();
    }, [fetchData])

    useEffect(() => {
        // In auth bypass mode, skip the logout-on-missing-user timeout
        if (isAuthBypass) {
            if (isAppLoaded && currentUser) {
                dispatch(getCompanySubscriptions(currentUser?.companyId));
            }
            return;
        }

        if (isAppLoaded && !currentUser) {
            const timer = setTimeout(async () => {
                await instance.logoutRedirect({
                    postLogoutRedirectUri: `${window.location.origin}/error-redirect`,
                });
            }, LOGOUT_TIMEOUT_TIMER);
            return () => clearTimeout(timer);
        }

        if (isAppLoaded && currentUser) {
            dispatch(getCompanySubscriptions(currentUser?.companyId));
        }
    }, [isAppLoaded, currentUser]);

    useEffect(() => {
        if (subscriptions) {
            setAppSubscription(subscriptions);
        }
    }, [subscriptions]);

    useEffect(() => {
        // Skip MSAL event listeners in auth bypass mode
        if (isAuthBypass) return;

        const handleLoginFailure = async (event) => {
            if (event.error && event.error.errorMessage.indexOf("AADB2C90118") > -1) {
                if (event.interactionType === InteractionType.Redirect) {
                    instance.loginRedirect(b2cPolicies.authorities.forgotPassword);
                } else if (event.interactionType === InteractionType.Popup) {
                    instance.loginPopup(b2cPolicies.authorities.forgotPassword)
                        .catch(() => {
                            return;
                        });
                }
            }
        };

        const handleLoginSuccess = async () => {
            console.log('post login');

            dispatch(getCurrentUser(instance, async () => {
                return instance.logoutRedirect({
                    postLogoutRedirectUri: `${window.location.origin}/error-redirect`,
                });
            }));

            fetchData();
        };

        const handleAcquireTokenSuccess = async (event) => {
            if (event?.payload) {
                let userId = event.payload.account?.username;
                let accessToken = event.payload.accessToken;
                let timestamp = event.timestamp;

                await SessionHandler.connect(userId, accessToken, timestamp);

                if (event.payload.idTokenClaims["tfp"] === b2cPolicies.names.forgotPassword) {
                    return instance.logout({
                        postLogoutRedirectUri: `${window.location.origin}/logout-redirect`,
                    });
                }
            }
        };

        const callbackId = instance.addEventCallback(async (event) => {
            switch (event.eventType) {
                case EventType.LOGIN_FAILURE:
                    await handleLoginFailure(event);
                    break;
                case Event.HANDLE_REDIRECT_END:
                    return instance.logout({
                        postLogoutRedirectUri: `${window.location.origin}/logout-redirect`,
                    });
                case EventType.LOGIN_SUCCESS:
                    await handleLoginSuccess();
                    break;
                case EventType.ACQUIRE_TOKEN_FAILURE:
                    await instance.loginRedirect(loginRequest);
                    break;
                case EventType.LOGOUT_SUCCESS:
                    console.log('post logout');
                    dispatch({ type: 'CLEAR_STORE' });
                    return;
                case EventType.ACQUIRE_TOKEN_SUCCESS:
                    await handleAcquireTokenSuccess(event);
                    break;
                default:
                    break;
            }
        });

        return () => {
            if (callbackId) {
                instance.removeEventCallback(callbackId);
            }
        };
    }, [dispatch, instance, fetchData]);

    if (!isAuthBypass && isAppLoaded && !currentUser) {
        return (<Loader />);
    }

    return (
        <div className="app">
            <AppLoadContext.Provider value={isAppLoaded}>
                <SubscriptionContext.Provider value={appSubscription}>
                    <AlertContext.Provider value={{ showAlert: handleShowAlert }}>
                        <AlertModal
                            alert={alert}
                            onHide={handleHideAlert}
                            onExited={handleExitAlert}
                        />
                        <SessionTimeout />
                        <Pages />
                    </AlertContext.Provider>
                </SubscriptionContext.Provider>
            </AppLoadContext.Provider>
        </div>
    );
}

export default App;
